import { useCallback, useEffect, useRef, useState } from "react";
import { createCustomer, deleteCustomer, getCustomerSites, listCustomers, updateCustomer } from "../../services/customer/customer.service";
import type { CreateCustomerRequest, Customer, CustomerSiteSummary } from "../../services/customer/customer.types";
import type { RuleFormData } from "../../components/forms/BuildingForm";
import type { RuleSummary } from "../../services/ruleManagement/ruleManagement.types";
import { createBuildingRules, getBuildingErrorMessage, loadBuildingRules } from "./buildingRules";
import type { CustomerWithSite } from "./buildingRules";

export interface CustomerDetails {
  sites: CustomerSiteSummary[];
  rules: RuleSummary[];
}

async function loadCustomerSites(customer: Customer): Promise<{ customer: CustomerWithSite; sites: CustomerSiteSummary[] }> {
  const response = await getCustomerSites(customer.id);
  if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to load sites");
  const primarySite = response.data[0];
  return {
    customer: { ...customer, primarySite, siteId: primarySite?.id, siteHierarchyId: primarySite?.siteHierarchyId },
    sites: response.data,
  };
}

export function useBuildings() {
  const [customers, setCustomers] = useState<CustomerWithSite[]>([]);
  const [details, setDetails] = useState<Record<number, CustomerDetails>>({});
  const [detailsLoading, setDetailsLoading] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    const load = async () => {
      try {
        const response = await listCustomers();
        if (current !== generation.current) return;
        if (!response.success || !response.data) throw new Error(response.error?.message || "Failed to load customers");
        const failures: string[] = [];
        const nextDetails: Record<number, CustomerDetails> = {};
        const enriched = await Promise.all(response.data.map(async customer => {
          try {
            const loaded = await loadCustomerSites(customer);
            const rules = await loadBuildingRules(loaded.customer.siteId);
            nextDetails[customer.id] = { sites: loaded.sites, rules };
            return loaded.customer;
          } catch (error: unknown) {
            failures.push(`${customer.customerName}: ${getBuildingErrorMessage(error)}`);
            return customer;
          }
        }));
        if (current !== generation.current) return;
        setCustomers(enriched);
        setDetails(nextDetails);
        if (failures.length) setError(failures.join("\n"));
      } catch (error: unknown) {
        if (current === generation.current) setError(getBuildingErrorMessage(error));
      } finally {
        if (current === generation.current) setLoading(false);
      }
    };
    void load();
    return () => { generation.current += 1; };
  }, []);

  const refreshCustomer = useCallback(async (customer: Customer): Promise<CustomerWithSite | null> => {
    const current = generation.current;
    setDetailsLoading(previous => ({ ...previous, [customer.id]: true }));
    try {
      const loaded = await loadCustomerSites(customer);
      const rules = await loadBuildingRules(loaded.customer.siteId);
      if (current !== generation.current) return null;
      setCustomers(previous => previous.map(item => item.id === customer.id ? loaded.customer : item));
      setDetails(previous => ({ ...previous, [customer.id]: { sites: loaded.sites, rules } }));
      return loaded.customer;
    } catch (error: unknown) {
      if (current === generation.current) setError(getBuildingErrorMessage(error));
      return null;
    } finally {
      if (current === generation.current) {
        setDetailsLoading(previous => ({ ...previous, [customer.id]: false }));
      }
    }
  }, []);

  const save = async (values: CreateCustomerRequest, rules: RuleFormData[] | null, editingCustomer: CustomerWithSite | null): Promise<boolean> => {
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const response = editingCustomer ? await updateCustomer(editingCustomer.id, values) : await createCustomer(values);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || `Failed to ${editingCustomer ? "update" : "create"} customer`);
      }
      const saved = response.data;
      setCustomers(previous => editingCustomer
        ? previous.map(customer => customer.id === saved.id ? { ...customer, ...saved } : customer)
        : [saved, ...previous]);
      setMapRefreshKey(previous => previous + 1);
      let message = `${saved.customerName || `Customer ${saved.id}`} ${editingCustomer ? "updated" : "created"}`;
      try {
        const loaded = await loadCustomerSites(saved);
        if (rules?.length) {
          if (loaded.customer.siteId && loaded.customer.siteHierarchyId) {
            const failures = await createBuildingRules(loaded.customer, rules);
            if (failures.length) throw new Error(`Failed to create ${failures.length} rule(s): ${failures.join(" | ")}`);
            message += " and rule(s) created";
          } else if (editingCustomer) {
            throw new Error("Cannot create rules: Site ID or site hierarchy ID is missing. The site may not be provisioned yet.");
          } else {
            message += ". Rules can be added after site provisioning (edit customer to add rules).";
          }
        }
        const siteRules = await loadBuildingRules(loaded.customer.siteId);
        setCustomers(previous => previous.map(customer => customer.id === saved.id ? loaded.customer : customer));
        setDetails(previous => ({ ...previous, [saved.id]: { sites: loaded.sites, rules: siteRules } }));
        setSuccess(message);
      } catch (error: unknown) {
        setError(`Customer saved. ${getBuildingErrorMessage(error)}`);
        await refreshCustomer(saved);
      }
      return true;
    } catch (error: unknown) {
      setError(getBuildingErrorMessage(error));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (customer: CustomerWithSite): Promise<void> => {
    setError(null);
    setSuccess(null);
    try {
      const response = await deleteCustomer(customer.id);
      if (!response.success) throw new Error(response.error?.message || "Failed to delete customer");
      setCustomers(previous => previous.filter(item => item.id !== customer.id));
      setMapRefreshKey(previous => previous + 1);
      setSuccess(`Successfully deleted ${customer.customerName || "customer"}`);
    } catch (error: unknown) {
      setError(getBuildingErrorMessage(error));
    }
  };

  return {
    customers, details, detailsLoading, loading, submitting, error, success, mapRefreshKey,
    save, remove, refreshCustomer, setError, setSuccess,
  };
}
